import React, { useEffect, useRef, useState } from 'react';
import { Badge } from '../services/badgeService';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/BadgeUnlockAnimation.css';

interface BadgeUnlockAnimationProps {
  badge: Badge;
  onComplete: () => void;
}

const BadgeUnlockAnimation: React.FC<BadgeUnlockAnimationProps> = ({ badge, onComplete }) => {
  const [stage, setStage] = useState<'intro' | 'reveal' | 'celebration'>('intro');
  const { t } = useTranslation();
  // onCompleteをrefで保持: 親の再レンダリングで新しい関数参照が渡されても
  // タイマーを再起動せず、常に最新のコールバックを呼び出せるようにする
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    // アニメーションのタイムライン
    // deps配列を空にすることでマウント時に1度だけ設定され、
    // 親の再レンダリングでタイマーがリセットされるバグを防ぐ
    const timeline = [
      { delay: 500, action: () => setStage('reveal') },
      { delay: 2000, action: () => setStage('celebration') },
      { delay: 5000, action: () => onCompleteRef.current() }
    ];

    const timers = timeline.map(({ delay, action }) =>
      setTimeout(action, delay)
    );

    // 効果音を再生（可能な場合）
    playUnlockSound();

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []); // マウント時のみ実行
  
  const playUnlockSound = () => {
    // 複数の音を組み合わせて壮大な効果音を作成
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // メインの解除音
    const oscillator1 = audioContext.createOscillator();
    const gainNode1 = audioContext.createGain();
    oscillator1.connect(gainNode1);
    gainNode1.connect(audioContext.destination);
    oscillator1.frequency.value = 523.25; // C5
    oscillator1.type = 'sine';
    gainNode1.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode1.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
    gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1);
    oscillator1.start(audioContext.currentTime);
    oscillator1.stop(audioContext.currentTime + 1);

    // ハーモニー音
    const oscillator2 = audioContext.createOscillator();
    const gainNode2 = audioContext.createGain();
    oscillator2.connect(gainNode2);
    gainNode2.connect(audioContext.destination);
    oscillator2.frequency.value = 659.25; // E5
    oscillator2.type = 'sine';
    gainNode2.gain.setValueAtTime(0, audioContext.currentTime + 0.1);
    gainNode2.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.2);
    gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1.2);
    oscillator2.start(audioContext.currentTime + 0.1);
    oscillator2.stop(audioContext.currentTime + 1.2);

    // 高音のキラキラ音
    const oscillator3 = audioContext.createOscillator();
    const gainNode3 = audioContext.createGain();
    oscillator3.connect(gainNode3);
    gainNode3.connect(audioContext.destination);
    oscillator3.frequency.value = 1046.5; // C6
    oscillator3.type = 'triangle';
    gainNode3.gain.setValueAtTime(0, audioContext.currentTime + 0.2);
    gainNode3.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.3);
    gainNode3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
    oscillator3.start(audioContext.currentTime + 0.2);
    oscillator3.stop(audioContext.currentTime + 0.8);

    // 最後のオシレーターが停止する1.2秒後にAudioContextを解放してリソースリークを防ぐ
    setTimeout(() => {
      audioContext.close().catch(() => {});
    }, 1500);
  };
  
  return (
    <div className="badge-unlock-overlay">
      <div className={`badge-unlock-container stage-${stage}`}>
        {stage === 'intro' && (
          <div className="intro-animation">
            <div className="pulse-ring"></div>
            <div className="pulse-ring delay-1"></div>
            <div className="pulse-ring delay-2"></div>
            <h2 className="unlock-text">{t('badges.newBadgeUnlocked')}</h2>
          </div>
        )}
        
        {stage === 'reveal' && (
          <div className="reveal-animation">
            <div className="badge-glow"></div>
            <div className="badge-container-animated">
              <div className="badge-icon-large">{badge.icon}</div>
              <h2 className="badge-name">{badge.name}</h2>
              <p className="badge-description">{badge.description}</p>
            </div>
            <div className="light-rays">
              {[...Array(12)].map((_, i) => (
                <div key={i} className={`light-ray ray-${i}`}></div>
              ))}
            </div>
          </div>
        )}
        
        {stage === 'celebration' && (
          <div className="celebration-animation">
            <div className="fireworks">
              {[...Array(20)].map((_, i) => (
                <div key={i} className={`firework firework-${i % 5}`}></div>
              ))}
            </div>
            <div className="confetti">
              {[...Array(50)].map((_, i) => (
                <div 
                  key={i} 
                  className={`confetti-piece confetti-${i % 6}`}
                  style={{
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 3}s`,
                    backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'][i % 6]
                  }}
                ></div>
              ))}
            </div>
            <div className="achievement-text">
              <h1>{t('badges.celebrationTitle')}</h1>
              <p>{t('badges.celebrationSubtitle')}</p>
              <div className="badge-final-display">
                <span className="badge-icon-final">{badge.icon}</span>
                <span className="badge-name-final">{badge.name}</span>
              </div>
            </div>
          </div>
        )}
        
        <div className="particle-effects">
          {[...Array(30)].map((_, i) => (
            <div 
              key={i} 
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BadgeUnlockAnimation;